import { defineStore } from "pinia";
import { Permissions, PermissionAction } from "@/constants/access";
import { onboardingStages, OnboardingStage } from "@/constants/onboarding";
import {
    loadOnboardingProgress,
    onboardingProgressStorageKey,
    saveOnboardingProgress,
    type OnboardingProgress,
} from "@/stores/onboardingProgressStorage";
import type { OrganisationProfile } from "@/types/school";

/**
 * Determines if the user can manage the organisation based on their permissions.
 */
const canManageOrganisation = ({ permissions }: OrganisationProfile) =>
    permissions[Permissions.BASE]?.includes(PermissionAction.VIEW) &&
    permissions[Permissions.ORG_ADMIN]?.includes(PermissionAction.VIEW) &&
    permissions[Permissions.ORG_ADMIN]?.includes(PermissionAction.EDIT);

/**
 * Checks if all organisations have signed the necessary contracts.
 */
const areContractsSigned = (organisations: OrganisationProfile[]): boolean =>
    organisations.every(
        ({ terms_conditions_signed, commercial_terms_signed }) =>
            terms_conditions_signed === true &&
            commercial_terms_signed === true,
    );

const hasContractRecords = (organisations: OrganisationProfile[]): boolean =>
    organisations.some(
        ({ terms_conditions_signed, commercial_terms_signed }) =>
            typeof terms_conditions_signed === "boolean" ||
            typeof commercial_terms_signed === "boolean",
    );

/**
 * Determines the initial onboarding progress based on the organisations and previously saved progress.
 * Used for initializing the onboarding state when the user first accesses the application.
 * @param organisations The list of organisations associated with the user.
 * @param savedProgress The previously saved onboarding progress, if any.
 * @returns An object containing the resolved progress, the required step, and the progress to save.
 */
const resolveInitialProgress = (
    organisations: OrganisationProfile[],
    savedProgress: OnboardingProgress | null,
): {
    progress: OnboardingProgress | null;
    requiredStep: OnboardingStage.CONTRACTS | null;
    progressToSave: OnboardingProgress | null;
} => {
    if (!hasContractRecords(organisations)) {
        return {
            progress: null,
            requiredStep: null,
            progressToSave: null,
        };
    }

    const requiredStep = areContractsSigned(organisations)
        ? null
        : OnboardingStage.CONTRACTS;
    const stageToSave =
        savedProgress?.stage === OnboardingStage.COMPLETED && requiredStep
            ? requiredStep
            : !savedProgress && requiredStep
              ? OnboardingStage.WELCOME
              : null;
    const progressToSave = stageToSave
        ? { version: 1 as const, stage: stageToSave }
        : null;

    return {
        progress: progressToSave ?? savedProgress,
        requiredStep,
        progressToSave,
    };
};

export const useOnboardingStore = defineStore("customer-onboarding", {
    state: () => ({
        progress: null as OnboardingProgress | null,
        storageKey: null as string | null,
        initialised: false,
        requiredStep: null as OnboardingStage.CONTRACTS | null,
    }),
    getters: {
        required: (state) => state.requiredStep === OnboardingStage.CONTRACTS,
        inProgress: (state) =>
            state.progress !== null &&
            (state.progress.stage !== OnboardingStage.COMPLETED ||
                state.requiredStep !== null),
        stage: (state): OnboardingStage =>
            state.progress?.stage === OnboardingStage.COMPLETED &&
            state.requiredStep
                ? state.requiredStep
                : (state.progress?.stage ?? OnboardingStage.WELCOME),
    },
    actions: {
        /**
         * Resolves the appropriate onboarding stage for the user.
         * @param requested The requested onboarding stage.
         * @returns The resolved onboarding stage based on the requested stage and current progress.
         */
        resolveStage(requested: unknown): OnboardingStage {
            const requestedIndex =
                typeof requested === "string"
                    ? onboardingStages.indexOf(requested as OnboardingStage)
                    : -1;
            return requestedIndex >= 0 &&
                requestedIndex <= onboardingStages.indexOf(this.stage)
                ? onboardingStages[requestedIndex]
                : this.stage;
        },
        /**
         * Initialises the onboarding process for the user.
         * @param email The email of the user.
         * @param organisations The list of organisations associated with the user.
         * @returns void
         */
        initialiseOnboarding(
            email: string,
            organisations: OrganisationProfile[],
        ) {
            this.storageKey = onboardingProgressStorageKey(email);
            this.progress = null;
            this.initialised = true;
            this.requiredStep = null;
            if (
                !organisations.length ||
                !organisations.every(canManageOrganisation)
            )
                return;

            const initialState = resolveInitialProgress(
                organisations,
                loadOnboardingProgress(this.storageKey),
            );
            this.progress = initialState.progress;
            this.requiredStep = initialState.requiredStep;
            if (initialState.progressToSave)
                this.saveProgress(initialState.progressToSave.stage);
        },
        /**
         *
         * @param stage The current onboarding stage.
         * @param organisations The list of organisations associated with the user.
         * @returns A boolean indicating whether the user can continue from the current stage.
         */
        canContinue(
            stage: OnboardingStage,
            organisations: OrganisationProfile[],
        ): boolean {
            if (!this.inProgress || !organisations.length) return false;
            if (
                stage === OnboardingStage.CONTRACTS ||
                stage === OnboardingStage.KYC ||
                stage === OnboardingStage.USERS
            ) {
                return areContractsSigned(organisations);
            }
            return (
                stage === OnboardingStage.WELCOME ||
                stage === OnboardingStage.ORGANISATIONS
            );
        },
        /**
         * Advances the onboarding stage to the next step if the current stage can be continued.
         * @param organisations The list of organisations associated with the user.
         * @returns A boolean indicating whether the onboarding stage was successfully advanced.
         */
        advance(organisations: OrganisationProfile[]): boolean {
            if (!this.canContinue(this.stage, organisations)) return false;
            const nextStage =
                onboardingStages[onboardingStages.indexOf(this.stage) + 1];
            if (!nextStage) return false;
            this.saveProgress(nextStage);
            this.requiredStep = areContractsSigned(organisations)
                ? null
                : OnboardingStage.CONTRACTS;
            return true;
        },
        /**
         * Saves the current onboarding progress to local storage.
         * @param stage The current onboarding stage to be saved.
         */
        saveProgress(stage: OnboardingStage) {
            if (!this.storageKey)
                throw new Error("Onboarding context is not loaded.");
            const progress: OnboardingProgress = { version: 1, stage };
            saveOnboardingProgress(this.storageKey, progress);
            this.progress = progress;
        },
        /**
         * Resets the onboarding state, clearing all progress and stored information.
         */
        reset() {
            this.progress = null;
            this.storageKey = null;
            this.initialised = false;
            this.requiredStep = null;
        },
    },
});
